import { useState } from "react";
import { api, fmtErr } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { Loader2, ShieldCheck, KeyRound, User } from "lucide-react";
import { toast } from "sonner";

export default function AccountSettings() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 10) return setErr("New password must be at least 10 characters.");
    if (!/[A-Za-z]/.test(next) || !/\d/.test(next)) return setErr("Include letters and at least one digit.");
    if (next !== confirm) return setErr("Confirmation does not match new password.");
    if (next === current) return setErr("New password must differ from your current password.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Session refreshed.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Account & Security</div>
        <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Your account</h1>
        <p className="text-sm text-slate-500 mt-1">Manage your credentials and session security for ConnectMyHub™.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-widest mb-3"><User size={13}/> Profile</div>
          <div className="text-sm">
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Name</div>
              <div className="text-slate-900 font-medium" data-testid="profile-name">{user?.name}</div>
            </div>
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Email</div>
              <div className="text-slate-900 font-medium" data-testid="profile-email">{user?.email}</div>
            </div>
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Role</div>
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-[11px] font-semibold" data-testid="profile-role">
                <ShieldCheck size={11}/> {user?.role?.replace('_',' ')}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} data-testid="password-change-form" className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center gap-2 text-slate-500 text-[11px] font-semibold uppercase tracking-widest mb-4"><KeyRound size={13}/> Change password</div>
          <p className="text-xs text-slate-500 mb-5">Passwords are hashed with bcrypt and never shown or logged. You will remain signed in — your session token will be rotated.</p>

          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Current password</label>
          <input data-testid="pw-current" type="password" autoComplete="current-password"
                 value={current} onChange={e=>setCurrent(e.target.value)}
                 className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-4 focus:border-sky-500 outline-none" required />

          <label className="block text-xs font-semibold text-slate-700 mb-1.5">New password</label>
          <input data-testid="pw-new" type="password" autoComplete="new-password"
                 value={next} onChange={e=>setNext(e.target.value)}
                 className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-1 focus:border-sky-500 outline-none" required />
          <div className="text-[11px] text-slate-500 mb-4">Minimum 10 characters. Include letters and at least one digit.</div>

          <label className="block text-xs font-semibold text-slate-700 mb-1.5">Confirm new password</label>
          <input data-testid="pw-confirm" type="password" autoComplete="new-password"
                 value={confirm} onChange={e=>setConfirm(e.target.value)}
                 className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm mb-4 focus:border-sky-500 outline-none" required />

          {err && <div data-testid="pw-error" className="mb-4 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

          <button data-testid="pw-submit" disabled={busy}
                  className="h-10 px-5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50 transition-colors">
            {busy && <Loader2 size={14} className="animate-spin"/>} Update password
          </button>
        </form>
      </div>
    </Layout>
  );
}
